import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { catchError, finalize, forkJoin, map, of } from 'rxjs';
import { AvatarFactory } from 'src/app/factory/avatar-factory';
import { HttpService } from 'src/app/http/http.service';
import { ImageService } from 'src/app/services/image.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { showFallbackHttpError } from 'src/app/shared/utils/http-error-toast.util';
import { ConstantUtil } from 'src/app/util/constant.util';

import { TenantContactDetailComponent } from '../item/tenant-contact-detail.component';
import { Channel, Status, TenantContactListRequest, TenantContactVO } from '../tenant-contact.types';

@Component({
  selector: 'tenant-contact-list',
  templateUrl: './tenant-contact-list.component.html',
  styleUrls: ['./tenant-contact-list.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzCardModule,
    NzTableModule,
    NzAvatarModule,
    NzEmptyModule,
    NzTagModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    NzFormModule,
    NzSpinModule,
    NzPaginationModule,
    I18nPipe,
    TestIdDirective,
    NzToolTipModule,
    NzPopconfirmModule,
    TenantContactDetailComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TenantContactListComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() tenantId!: string;
  @Input() tenantType?: string;
  @Input() serviceNumberId?: string;
  @Input() pageTitleKey = 'tenant.detail.contacts';
  @Input() embeddedMode = false;

  @ViewChild('searchInputElement') searchInputElement?: ElementRef<HTMLInputElement>;
  @ViewChild('mobileScrollContainer') mobileScrollContainer?: ElementRef<HTMLElement>;
  @ViewChild('mobileLoadMoreAnchor') mobileLoadMoreAnchor?: ElementRef<HTMLDivElement>;

  private readonly http = inject(HttpService);
  private readonly imageService = inject(ImageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly message = inject(NzMessageService);
  private readonly ngZone = inject(NgZone);
  public readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  public readonly AvatarFactory = AvatarFactory;

  loading = false;
  listOfData: TenantContactVO[] = [];
  pageIndex = 1;
  pageSize = 20;
  total = 0;
  key = '';
  refreshTime = Date.now();
  direction: 'asc' | 'desc' = 'desc';
  isMobileView = false;
  isLoadingMore = false;
  hasMoreMobile = false;
  mobileSearchExpanded = true;
  detailVisible = false;
  selectedContact: TenantContactVO | null = null;

  cleaningId?: string;
  private mobileLoadObserver?: IntersectionObserver;

  @HostBinding('class.embedded-mode')
  get isEmbeddedMode(): boolean {
    return this.embeddedMode;
  }

  get trimmedKey(): string {
    return this.key?.trim() || '';
  }

  ngOnInit(): void {
    this.updateViewportMode();
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.syncMobileLoadObserver();
  }

  ngOnDestroy(): void {
    this.mobileLoadObserver?.disconnect();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewportMode(true);
  }

  loadData(options?: { append?: boolean; pageIndex?: number }): void {
    const append = !!options?.append;
    const requestPageIndex = options?.pageIndex ?? this.pageIndex;

    if (append) {
      this.isLoadingMore = true;
    } else {
      this.loading = true;
    }
    const payload: TenantContactListRequest = {
      tenantId: this.tenantId,
      pageIndex: Math.max(0, requestPageIndex - 1),
      pageSize: this.pageSize,
      key: this.key?.trim() || undefined,
      refreshTime: this.refreshTime,
      direction: this.direction
    };

    this.http
      .post(ConstantUtil.TENANT_CONTACT_V1_LIST, payload)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.isLoadingMore = false;
          this.cdr.detectChanges();
          this.syncMobileLoadObserver();
        })
      )
      .subscribe(res => {
        if (res.success) {
          const data = res.data || {};
          const nextItems = ((data.items ?? data.list ?? []) as TenantContactVO[]).map(item => this.decorateContact(item));
          this.pageIndex = requestPageIndex;
          this.listOfData = append ? [...this.listOfData, ...nextItems] : nextItems;
          this.total = data.totalCount ?? this.listOfData.length;
          this.preloadAvatarUrls(nextItems);
          this.updateMobileLoadState();
          this.cdr.detectChanges();
        } else {
          showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('tenant.contact.load.failed'));
          this.updateMobileLoadState();
        }
      });
  }

  private decorateContact(item: TenantContactVO): TenantContactVO {
    return {
      ...item,
      avatarUrl: this.normalizeAvatarUrl(item?.avatarUrl),
      fallbackAvatarUrl: AvatarFactory.defaultAvatarWithName(item?.name || item?.alias || item?.id || 'U')
    };
  }

  private preloadAvatarUrls(contacts: TenantContactVO[]): void {
    const requests = contacts
      .filter(item => item?.avatarId && !item?.avatarUrl)
      .map(item =>
        this.imageService.loadImage(String(item.avatarId), 'S').pipe(
          map(avatarUrl => ({ item, avatarUrl })),
          catchError(() => of({ item, avatarUrl: '' }))
        )
      );

    if (!requests.length) {
      return;
    }

    forkJoin(requests).subscribe(results => {
      results.forEach(({ item, avatarUrl }) => {
        if (avatarUrl) {
          item.avatarUrl = avatarUrl;
        }
      });
      this.cdr.detectChanges();
    });
  }

  onSearch(): void {
    this.pageIndex = 1;
    this.key = this.trimmedKey;
    this.refreshTime = Date.now();
    this.loadData();
  }

  resetSearch(): void {
    this.key = '';
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.mobileSearchExpanded = !this.isMobileView;
    this.loadData();
  }

  clearFilter(type: string): void {
    if (type === 'keywords') {
      this.resetSearch();
    }
  }

  onRefresh(): void {
    this.refreshTime = Date.now();
    this.pageIndex = 1;
    this.loadData();
  }

  toggleMobileSearch(): void {
    if (!this.isMobileView) {
      return;
    }

    this.mobileSearchExpanded = !this.mobileSearchExpanded;
    if (this.mobileSearchExpanded) {
      setTimeout(() => this.searchInputElement?.nativeElement.focus(), 0);
    }
  }

  onPageIndexChange(index: number): void {
    this.pageIndex = index;
    this.loadData();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageIndex = 1;
    this.loadData();
  }

  openDetail(item: TenantContactVO): void {
    if (!item.id) return;
    this.selectedContact = item;
    this.detailVisible = true;
  }

  closeDetail(): void {
    this.detailVisible = false;
    this.selectedContact = null;
  }

  cleanContact(item: TenantContactVO): void {
    if (!item?.id) {
      this.message.error(this.i18n.fanyi('common.id-missing'));
      return;
    }
    this.cleaningId = item.id;
    const url = ConstantUtil.TENANT_CONTACT_V1_CLEAN.replace('{id}', item.id);
    this.http
      .post(url, {})
      .pipe(
        finalize(() => {
          this.cleaningId = undefined;
          this.refreshTime = Date.now();
          this.pageIndex = 1;
          this.loadData();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.message.success(this.i18n.fanyi('tenant.contact.clean.success'));
        } else {
          showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('tenant.contact.clean.failed'));
        }
      });
  }

  getStatusColor(status?: Status): string {
    switch (status) {
      case Status.Enable:
        return 'green';
      case Status.Disable:
        return 'red';
      case Status.Delete:
        return 'volcano';
      case Status.Audit:
        return 'blue';
      case Status.New:
        return 'gold';
      default:
        return 'default';
    }
  }

  getStatusTextKey(status?: Status): string {
    switch (status) {
      case Status.Enable:
        return 'theme.status.enable';
      case Status.Disable:
        return 'theme.status.disable';
      case Status.New:
        return 'theme.status.new';
      case Status.Delete:
        return 'account.tenant.status.deleted';
      case Status.Audit:
        return 'theme.status.audit';
      default:
        return 'account.tenant.status.unknown';
    }
  }

  getChannelColor(channel?: Channel): string {
    // simple mapping; customize as needed
    const map: Record<string, string> = {
      Line: 'green',
      Facebook: 'blue',
      Google: 'geekblue',
      Instagram: 'magenta',
      Webchat: 'cyan',
      Admin: 'gold',
      Internal: 'purple'
    };
    return map[channel as string] || 'default';
  }

  getStatusPillClass(status?: Status): string {
    switch (status) {
      case Status.Enable:
        return 'status-pill-enable';
      case Status.Disable:
        return 'status-pill-disable';
      case Status.Delete:
        return 'status-pill-delete';
      case Status.Audit:
        return 'status-pill-audit';
      case Status.New:
        return 'status-pill-new';
      default:
        return 'status-pill-default';
    }
  }

  getDisplayName(item: TenantContactVO): string {
    return item.name || item.alias || this.i18n.fanyi('common.unknown');
  }

  trackByContact(_: number, item: TenantContactVO): string {
    return String(item.id || item.accountId || item.openId || item.phone || item.alias || item.name || _);
  }

  loadMoreOnMobile(): void {
    if (!this.isMobileView || this.loading || this.isLoadingMore || !this.hasMoreMobile) {
      return;
    }

    this.loadData({
      append: true,
      pageIndex: this.pageIndex + 1
    });
  }

  private updateViewportMode(shouldReload = false): void {
    const nextMobile = typeof window !== 'undefined' && window.innerWidth <= 767;
    const modeChanged = this.isMobileView !== nextMobile;
    this.isMobileView = nextMobile;
    this.mobileSearchExpanded = !nextMobile || !!this.trimmedKey;

    if (modeChanged && shouldReload) {
      this.pageIndex = 1;
      this.refreshTime = Date.now();
      this.loadData();
      return;
    }

    this.updateMobileLoadState();
  }

  private updateMobileLoadState(): void {
    this.hasMoreMobile = this.isMobileView && this.listOfData.length < this.total;
    this.syncMobileLoadObserver();
  }

  private syncMobileLoadObserver(): void {
    this.mobileLoadObserver?.disconnect();

    if (!this.isMobileView || !this.hasMoreMobile || !this.mobileLoadMoreAnchor?.nativeElement) {
      return;
    }

    this.mobileLoadObserver = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.ngZone.run(() => this.loadMoreOnMobile());
        }
      },
      {
        root: this.mobileScrollContainer?.nativeElement ?? null,
        rootMargin: '0px 0px 220px 0px',
        threshold: 0.01
      }
    );

    this.mobileLoadObserver.observe(this.mobileLoadMoreAnchor.nativeElement);
  }

  private normalizeAvatarUrl(rawUrl?: string): string {
    const normalized = String(rawUrl ?? '').trim();
    return normalized || '';
  }
}
